import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import axios, { type AxiosInstance } from "axios";
import type { User, AuthContextType } from "@/types";
import { logApiFailure, setClientLogEndpoint } from "@/lib/clientLog";
import { resolveNxivoAdmin } from "@/lib/nxivo";

const resolvePublicBasePath = (): string => {
  const publicUrl = process.env.PUBLIC_URL?.trim();
  if (!publicUrl || publicUrl === "/") return "";

  return publicUrl.replace(/\/+$/, "");
};

const resolveBackendUrl = (): string => {
  const configured = process.env.REACT_APP_BACKEND_URL?.trim();

  // In browser/runtime, prefer same-origin for deployed environments so OAuth
  // callbacks and API requests stay on the current domain.
  if (typeof window !== "undefined") {
    const sameOrigin = `${window.location.origin}${resolvePublicBasePath()}`;
    const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

    // Local CRA has no /api proxy — always use the Bindley API port unless configured.
    if (isLocalHost) {
      return (configured || "http://localhost:8001").replace(/\/+$/, "");
    }

    if (!configured) return sameOrigin;

    try {
      const parsed = new URL(configured);
      if (parsed.host === window.location.host) return parsed.toString().replace(/\/+$/, "");
      return sameOrigin;
    } catch {
      return sameOrigin;
    }
  }

  if (configured) return configured.replace(/\/+$/, "");

  return "http://localhost:8001";
};

const API = `${resolveBackendUrl()}/api`;
setClientLogEndpoint(API);

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("lms_token"));
  const [loading, setLoading] = useState(true);

  const api = useCallback((): AxiosInstance => {
    const instance = axios.create({ baseURL: API });
    if (token) {
      instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        logApiFailure(err);
        if (err.response?.status === 401) {
          logout();
        }
        return Promise.reject(err);
      },
    );
    return instance;
  }, [token]);

  const fetchMe = useCallback(async () => {
    if (!token) {
      // Try silent SSO from nxivo-admin cookie before giving up
      const nxivoAdmin = resolveNxivoAdmin();
      if (!nxivoAdmin) {
        setLoading(false);
        return;
      }
      try {
        const launchRes = await fetch(`${nxivoAdmin.api}/api/auth/silent-launch`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ appKey: "bindley" }),
        });
        if (launchRes.ok) {
          const launch = await launchRes.json();
          const sessionRes = await axios.post(`${API}/auth/session`, {
            accessToken: launch.tokens.accessToken,
            refreshToken: launch.tokens.refreshToken,
          });
          localStorage.setItem("lms_token", sessionRes.data.token);
          setToken(sessionRes.data.token);
          setUser(sessionRes.data.user);
          setLoading(false);
          return;
        }
      } catch {
        /* no SSO session */
      }
      setLoading(false);
      return;
    }
    try {
      const res = await api().get("/auth/me");
      setUser(res.data);
    } catch {
      localStorage.removeItem("lms_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const refreshMe = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const login = async (username: string, password: string): Promise<User> => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem("lms_token", t);
    setToken(t);
    setUser(u);
    return u;
  };

  const completeOAuthLogin = async (authToken: string, userFromSession?: User): Promise<User> => {
    localStorage.setItem("lms_token", authToken);
    setToken(authToken);

    if (userFromSession) {
      setUser(userFromSession);
      return userFromSession;
    }

    const instance = axios.create({ baseURL: API });
    instance.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

    const me = await instance.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const loginWithGoogleToken = async (googleToken: string): Promise<User> => {
    const res = await axios.post(`${API}/auth/google/callback`, { token: googleToken });
    const { token: authToken, user: loggedInUser } = res.data;
    localStorage.setItem("lms_token", authToken);
    setToken(authToken);
    setUser(loggedInUser);
    return loggedInUser;
  };

  const beginMicrosoftLogin = () => {
    window.location.href = `${API}/auth/microsoft`;
  };

  const logout = () => {
    localStorage.removeItem("lms_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        loginWithGoogleToken,
        beginMicrosoftLogin,
        completeOAuthLogin,
        refreshMe,
        logout,
        loading,
        api: api(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
